import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Cpu } from 'lucide-react';
import { Seo } from '../components/Seo';
import { PADDLES, TECH_FAQ, TECH_IMG } from '../data/technicals';
import { ComparisonTable, PaddleSelector, PaddleCtas, TechFaq, DataReference } from '../components/technicals/TechBlocks';

const SITE = 'https://dyssc.com';
const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-10% 0px' },
  transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Compare() {
  const faq = TECH_FAQ.slice(0, 4);
  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: faq.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
  ];
  return (
    <div className="bg-[#0B0B0C] text-white" data-testid="compare-page">
      <Seo
        title="Compare Paddles – HexCore vs ProFoam | DYSSC"
        description="HexCore and ProFoam at a glance: core, weight, swing weight, twist weight, thickness, bending and impact-sound data side by side. Find the DYSSC paddle that suits your game."
        canonical={`${SITE}/compare`}
        image={TECH_IMG.cutaway}
        jsonLd={jsonLd}
      />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_60%_at_15%_20%,rgba(224,33,40,0.14),transparent_60%),radial-gradient(ellipse_50%_60%_at_85%_20%,rgba(30,111,217,0.16),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-16 pb-8">
          <motion.p {...fade(0)} className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.32em] font-bold text-zinc-400">Compare paddles</motion.p>
          <motion.h1 {...fade(0.1)} className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] tracking-tight">
            <span style={{ color: PADDLES.hexcore.accent }}>HexCore</span> <span className="text-zinc-500">vs</span> <span style={{ color: PADDLES.profoam.accent }}>ProFoam</span>
          </motion.h1>
          <motion.p {...fade(0.2)} className="mt-4 text-zinc-300 text-base md:text-lg max-w-2xl">
            Prefer a firmer feel and a bit more weight in the hand? Explore HexCore. Prefer a lighter, quicker paddle? Explore ProFoam. Compare the numbers and choose your DYSSC.
          </motion.p>
          <motion.div {...fade(0.3)} className="mt-6">
            <Link to="/technicals" className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white underline underline-offset-4 decoration-white/30 hover:decoration-white transition-colors" data-testid="compare-to-technicals">
              <Cpu className="w-4 h-4" /> See the full technical analysis <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14 md:pb-20">
        <motion.div {...fade(0.1)}><ComparisonTable /></motion.div>
        <motion.div {...fade(0.1)} className="mt-8"><PaddleCtas compare={false} /></motion.div>
      </section>

      <section className="border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20">
          <motion.div {...fade(0)} className="max-w-2xl mb-10">
            <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.32em] font-bold text-zinc-400">Preference selector</p>
            <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl">Not sure? Start from what you prefer.</h2>
          </motion.div>
          <PaddleSelector />
        </div>
      </section>

      <section className="border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.32em] font-bold text-zinc-400">Quick answers</p>
            <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl">Clear answers that give both models an identity</h2>
            <div className="mt-8"><DataReference /></div>
          </div>
          <div className="lg:col-span-7"><TechFaq items={faq} /></div>
        </div>
      </section>
    </div>
  );
}
